import React, { useMemo } from 'react';
import { ActivityIndicator, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import Svg, { Circle, Line, Path, Rect } from 'react-native-svg';

import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';
import { EVENT_FONT_CONDENSED } from '../Event/eventTheme';

/**
 * The profile's paired action buttons from the private chat mockups: BLOCK CHAT / PRIVATE CHAT on
 * another player's profile, BLOCK LIST / MY PRIVATE CHATS on your own. A red or a gold plate with
 * a glowing rim, an icon and an upper-case label. Numbers are in the 390-wide design space.
 */
type Variant = 'red' | 'gold';

type Props = {
	label: string;
	icon: (color: string, size: number) => React.ReactNode;
	variant: Variant;
	scale: number;
	onPress?: () => void;
	busy?: boolean;
	accessibilityLabel?: string;
	/** Shares a row with its pair (the default); false for a button standing on its own line. */
	fill?: boolean;
	style?: StyleProp<ViewStyle>;
};

const LOOK: Record<
	Variant,
	{ fill: Array<[string, string]>; rim: string; glow: string; ink: string }
> = {
	red: {
		fill: [
			['0', '#D9311F'],
			['0.55', '#A3140C'],
			['1', '#6E0705'],
		],
		rim: '#FF8F7E',
		glow: 'rgba(255, 77, 61, 0.55)',
		ink: '#FFE9DC',
	},
	gold: {
		fill: [
			['0', '#FFF5B9'],
			['0.35', '#FFE16A'],
			['0.78', '#F5A300'],
			['1', '#CE7C00'],
		],
		rim: '#FFE45C',
		glow: 'rgba(255, 197, 0, 0.6)',
		ink: '#2A0A04',
	},
};

// Half of the 322 profile body leaves about 110 for the label: a long one ("MY PRIVATE CHATS",
// and longer translations) goes to a second line rather than being cut.
const BUTTON = { height: 44, radius: 10, paddingX: 8, gap: 6, icon: 20, label: 15 };

export const ProfileActionButton = ({
	label,
	icon,
	variant,
	scale,
	onPress,
	busy = false,
	accessibilityLabel,
	fill = true,
	style,
}: Props) => {
	const styles = useMemo(() => createStyles(scale, variant), [scale, variant]);
	const look = LOOK[variant];

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={accessibilityLabel ?? label}
			accessibilityState={{ busy }}
			onPress={busy ? undefined : onPress}
			style={[styles.button, fill && styles.fill, style]}
		>
			<GradientFill stops={look.fill} radius={BUTTON.radius * scale} />
			{busy ? (
				<ActivityIndicator color={look.ink} />
			) : (
				<View style={styles.content}>
					{icon(look.ink, BUTTON.icon * scale)}
					<Text numberOfLines={2} style={styles.label}>
						{label.toUpperCase()}
					</Text>
				</View>
			)}
		</Pressable>
	);
};

const createStyles = (scale: number, variant: Variant) => {
	const s = (value: number) => value * scale;
	const look = LOOK[variant];
	return StyleSheet.create({
		button: {
			height: s(BUTTON.height),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(BUTTON.paddingX),
			borderRadius: s(BUTTON.radius),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: look.rim,
			boxShadow: `0px 0px ${s(8)}px 0px ${look.glow}`,
		},
		fill: { flex: 1 },
		content: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			columnGap: s(BUTTON.gap),
			maxWidth: '100%',
		},
		label: {
			flexShrink: 1,
			color: look.ink,
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: s(BUTTON.label),
			lineHeight: s(BUTTON.label * 1.05),
			fontWeight: '800',
			letterSpacing: s(0.2),
			textAlign: 'center',
		},
	});
};

// One SVG each, so the thin strokes stay crisp on web.

/** A no-entry sign: BLOCK CHAT. */
export const NoEntryIcon = (color: string, size: number) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Circle cx='12' cy='12' r='9' stroke={color} strokeWidth='2.2' />
		<Line x1='5.8' y1='5.8' x2='18.2' y2='18.2' stroke={color} strokeWidth='2.2' />
	</Svg>
);

/** A speech bubble with three dots: PRIVATE CHAT. */
export const ChatBubbleIcon = (color: string, size: number) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M5 4.5h14a2.5 2.5 0 0 1 2.5 2.5v8a2.5 2.5 0 0 1-2.5 2.5h-6.2L8.5 21v-3.5H5A2.5 2.5 0 0 1 2.5 15V7A2.5 2.5 0 0 1 5 4.5Z'
			stroke={color}
			strokeWidth='2'
			strokeLinejoin='round'
		/>
		<Circle cx='8' cy='11' r='1.3' fill={color} />
		<Circle cx='12' cy='11' r='1.3' fill={color} />
		<Circle cx='16' cy='11' r='1.3' fill={color} />
	</Svg>
);

/** A contact card: BLOCK LIST. */
export const ContactCardIcon = (color: string, size: number) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Rect x='2.5' y='5' width='19' height='14' rx='2.5' stroke={color} strokeWidth='2' />
		<Circle cx='8' cy='10.5' r='2' fill={color} />
		<Path d='M4.8 16c.5-1.8 1.7-2.7 3.2-2.7s2.7.9 3.2 2.7' fill={color} />
		<Line
			x1='13.5'
			y1='9.5'
			x2='18.5'
			y2='9.5'
			stroke={color}
			strokeWidth='1.8'
			strokeLinecap='round'
		/>
		<Line
			x1='13.5'
			y1='12.5'
			x2='18.5'
			y2='12.5'
			stroke={color}
			strokeWidth='1.8'
			strokeLinecap='round'
		/>
		<Line
			x1='13.5'
			y1='15.5'
			x2='17'
			y2='15.5'
			stroke={color}
			strokeWidth='1.8'
			strokeLinecap='round'
		/>
	</Svg>
);

/** Two speech bubbles: MY PRIVATE CHATS. */
export const ChatsIcon = (color: string, size: number) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M10 3.5c-4 0-7 2.4-7 5.5 0 1.4.6 2.6 1.6 3.6L4 15.5l3.3-1.5c.8.3 1.7.5 2.7.5'
			stroke={color}
			strokeWidth='1.9'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
		<Path
			d='M15 8c-3.9 0-7 2.4-7 5.5S11.1 19 15 19c.9 0 1.8-.1 2.6-.4l3.4 1.5-.6-3c1-.9 1.6-2.2 1.6-3.6C22 10.4 18.9 8 15 8Z'
			stroke={color}
			strokeWidth='1.9'
			strokeLinejoin='round'
		/>
		<Circle cx='12' cy='13.5' r='1.1' fill={color} />
		<Circle cx='15' cy='13.5' r='1.1' fill={color} />
		<Circle cx='18' cy='13.5' r='1.1' fill={color} />
	</Svg>
);

export default ProfileActionButton;
